/**
 * HostelRoomInventory.jsx — CAMP-RES-DEMO-01
 * Premium visual room catalogue for Kaveri Bhavan.
 * [DEMO] Illustrative accommodation imagery — not actual VCE facilities.
 */
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Building2, BedDouble, Search, SlidersHorizontal, Wifi, Wind,
  Ruler, ChevronRight, ArrowLeft, Star, CheckCircle2, AlertTriangle,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const VCE_INST = "44444444-4444-4444-4444-444444444444";

const ROOM_IMAGES = {
  SUITE:    "https://images.unsplash.com/photo-1629140727571-9b5c6f6267b4?crop=entropy&cs=srgb&fm=jpg&w=600&q=80",
  DELUXE:   "https://images.unsplash.com/photo-1737517302831-e7b8a8eaa97c?crop=entropy&cs=srgb&fm=jpg&w=600&q=80",
  SHARING:  "https://images.unsplash.com/photo-1576354302919-96748cb8299e?crop=entropy&cs=srgb&fm=jpg&w=600&q=80",
  AC:       "https://images.unsplash.com/photo-1758974770768-552d7bb1dc63?crop=entropy&cs=srgb&fm=jpg&w=600&q=80",
  STANDARD: "https://images.unsplash.com/photo-1552858725-693709cc17c7?crop=entropy&cs=srgb&fm=jpg&w=600&q=80",
  NON_AC:   "https://images.unsplash.com/photo-1611971263023-105938ce12ed?crop=entropy&cs=srgb&fm=jpg&w=600&q=80",
};

const CATEGORY_LABELS = {
  SUITE:    "Signature Studio / Executive Apt",
  DELUXE:   "Deluxe Single / Premium Twin",
  SHARING:  "Premium Shared Residence",
  AC:       "Accessible Premium Room",
  STANDARD: "Standard Room",
  NON_AC:   "Non-AC Room",
};

const STATUS_CONFIG = {
  AVAILABLE:          { label: "Available",          dot: "bg-emerald-400", badge: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" },
  PARTIALLY_OCCUPIED: { label: "Partially Occupied", dot: "bg-amber-400",   badge: "bg-amber-500/20 text-amber-300 border-amber-500/30" },
  FULLY_OCCUPIED:     { label: "Fully Occupied",     dot: "bg-red-400",     badge: "bg-red-500/20 text-red-300 border-red-500/30" },
  RESERVED:           { label: "Reserved",           dot: "bg-blue-400",    badge: "bg-blue-500/20 text-blue-300 border-blue-500/30" },
  MAINTENANCE:        { label: "Maintenance",        dot: "bg-orange-400",  badge: "bg-orange-500/20 text-orange-300 border-orange-500/30" },
  OUT_OF_SERVICE:     { label: "Out of Service",     dot: "bg-slate-400",   badge: "bg-slate-500/20 text-slate-300 border-slate-500/30" },
};

export default function HostelRoomInventory() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("ALL");
  const [filterStatus, setFilterStatus] = useState("ALL");
  const [selected, setSelected] = useState(null);

  const institutionId = user?.institution_id || VCE_INST;

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    axios
      .get(`${API}/api/v1/hostel/rooms?institution_id=${institutionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => { setRooms(r.data?.rooms || r.data || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token, institutionId]);

  const filtered = useMemo(() => {
    let list = rooms;
    if (filterCat !== "ALL") list = list.filter((r) => r.room_category === filterCat);
    if (filterStatus !== "ALL") list = list.filter((r) => r.status === filterStatus);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(
        (r) => r.room_number?.toLowerCase().includes(q) ||
               r.room_category?.toLowerCase().includes(q) ||
               r.notes?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [rooms, filterCat, filterStatus, search]);

  const categories = [...new Set(rooms.map((r) => r.room_category))];
  const statuses = [...new Set(rooms.map((r) => r.status))];

  return (
    <div className="p-6 space-y-5" data-testid="hostel-room-inventory">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/campus/hostel")}
            className="text-slate-400 hover:text-white" data-testid="btn-back-dashboard">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <BedDouble className="w-5 h-5 text-amber-400" />
              <h1 className="text-xl font-bold text-white">Accommodation Catalogue</h1>
            </div>
            <p className="text-slate-400 text-sm">Kaveri Bhavan · {rooms.length} rooms</p>
            <p className="text-xs text-slate-500">[DEMO] Illustrative accommodation imagery — not actual VCE facilities</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center" data-testid="room-filters">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            data-testid="room-search-input"
            placeholder="Search rooms…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-slate-800 border-slate-600 text-white placeholder-slate-500"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {["ALL", ...categories].map((cat) => (
            <button
              key={cat}
              data-testid={`filter-cat-${cat}`}
              onClick={() => setFilterCat(cat)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                filterCat === cat
                  ? "bg-amber-600 text-white border-amber-500"
                  : "bg-slate-800 text-slate-400 border-slate-600 hover:border-amber-500"
              }`}
            >
              {cat === "ALL" ? "All Categories" : CATEGORY_LABELS[cat] || cat}
            </button>
          ))}
        </div>
        <div className="flex gap-2 flex-wrap">
          {["ALL", ...statuses].map((s) => {
            const sc = STATUS_CONFIG[s];
            return (
              <button
                key={s}
                data-testid={`filter-status-${s}`}
                onClick={() => setFilterStatus(s)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all flex items-center gap-1.5 ${
                  filterStatus === s
                    ? "bg-slate-600 text-white border-slate-400"
                    : "bg-slate-800 text-slate-400 border-slate-600 hover:border-slate-400"
                }`}
              >
                {sc && <span className={`w-2 h-2 rounded-full ${sc.dot}`} />}
                {s === "ALL" ? "All Statuses" : sc?.label || s}
              </button>
            );
          })}
        </div>
      </div>

      {/* Summary counts */}
      <div className="text-sm text-slate-400">
        Showing <span className="text-white font-medium">{filtered.length}</span> of{" "}
        <span className="text-white font-medium">{rooms.length}</span> rooms
      </div>

      {/* Room Grid */}
      {loading ? (
        <div className="text-center py-12 text-slate-400">
          <BedDouble className="w-8 h-8 mx-auto mb-2 animate-pulse" />
          Loading accommodation catalogue…
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="room-grid">
          {filtered.map((room) => (
            <RoomCard
              key={room.id}
              room={room}
              onSelect={() => setSelected(room.id === selected ? null : room.id)}
              expanded={selected === room.id}
              onBook={() => navigate(`/campus/hostel/bookings/new?room_id=${room.id}`)}
            />
          ))}
          {filtered.length === 0 && (
            <div className="col-span-3 text-center py-16 text-slate-500">
              No rooms match your filters.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function RoomCard({ room, onSelect, expanded, onBook }) {
  const sc = STATUS_CONFIG[room.status] || { label: room.status, dot: "bg-slate-400", badge: "bg-slate-500/20 text-slate-300 border-slate-500/30" };
  const imgUrl = ROOM_IMAGES[room.room_category] || ROOM_IMAGES.STANDARD;
  const amenities = Array.isArray(room.amenities) ? room.amenities : [];
  const keyAmenities = amenities
    .filter((a) => !a.startsWith("Illustrative"))
    .slice(0, 4);

  return (
    <div
      data-testid={`room-card-${room.id}`}
      className="bg-slate-800/60 border border-slate-700/50 rounded-xl overflow-hidden hover:border-amber-500/40 transition-all group"
    >
      {/* Image */}
      <div className="relative h-44 overflow-hidden">
        <img
          src={imgUrl}
          alt={`${CATEGORY_LABELS[room.room_category] || room.room_category} — Illustrative`}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent" />
        <div className="absolute bottom-2 left-3 flex items-center gap-2">
          <Badge className={`text-xs border ${sc.badge}`}>
            <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${sc.dot}`} />
            {sc.label}
          </Badge>
        </div>
        <div className="absolute top-2 right-2">
          <Badge className="bg-black/60 text-white border-white/20 text-xs">
            Room {room.room_number}
          </Badge>
        </div>
        <div className="absolute top-2 left-2">
          <p className="text-xs text-white/60 bg-black/40 rounded px-2 py-0.5">Demo imagery</p>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div>
            <p className="font-semibold text-white text-sm">
              {CATEGORY_LABELS[room.room_category] || room.room_category}
            </p>
            <p className="text-xs text-slate-400">
              {room.furnishing?.replace("_", " ")} · Capacity: {room.capacity}
            </p>
          </div>
          <div className="text-right">
            {room.area_sqft && (
              <p className="text-xs text-slate-400 flex items-center gap-1 justify-end">
                <Ruler className="w-3 h-3" /> {room.area_sqft} sqft
              </p>
            )}
          </div>
        </div>

        {/* Icons row */}
        <div className="flex gap-3 mb-3 text-xs text-slate-400">
          <span className="flex items-center gap-1"><Wifi className="w-3 h-3 text-blue-400" /> WiFi</span>
          <span className="flex items-center gap-1"><Wind className="w-3 h-3 text-cyan-400" /> AC</span>
          <span className="flex items-center gap-1"><BedDouble className="w-3 h-3 text-amber-400" /> {room.capacity} Bed{room.capacity > 1 ? "s" : ""}</span>
        </div>

        {/* Key Amenities */}
        {keyAmenities.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {keyAmenities.map((a) => (
              <span key={a} className="text-xs bg-slate-700/50 text-slate-300 px-2 py-0.5 rounded-full">
                {a.length > 28 ? a.slice(0, 26) + "…" : a}
              </span>
            ))}
            {amenities.filter((a) => !a.startsWith("Illustrative")).length > 4 && (
              <span className="text-xs text-slate-500 px-2 py-0.5">
                +{amenities.filter((a) => !a.startsWith("Illustrative")).length - 4} more
              </span>
            )}
          </div>
        )}

        {/* Expand button + Book */}
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            className="flex-1 border-slate-600 text-slate-300 hover:text-white hover:border-amber-500"
            onClick={onSelect}
            data-testid={`btn-room-details-${room.id}`}
          >
            {expanded ? "Less" : "Details"} <ChevronRight className={`w-3 h-3 ml-1 transition-transform ${expanded ? "rotate-90" : ""}`} />
          </Button>
          {room.status === "AVAILABLE" && (
            <Button
              size="sm"
              className="flex-1 bg-amber-600 hover:bg-amber-500 text-white"
              onClick={onBook}
              data-testid={`btn-book-room-${room.id}`}
            >
              Book
            </Button>
          )}
        </div>

        {/* Expanded Details */}
        {expanded && (
          <div className="mt-3 pt-3 border-t border-slate-700/50 space-y-2">
            <p className="text-xs font-medium text-slate-300">All Amenities</p>
            <div className="flex flex-wrap gap-1">
              {amenities
                .filter((a) => !a.startsWith("Illustrative"))
                .map((a) => (
                  <span key={a} className="text-xs bg-slate-700/40 text-slate-300 px-2 py-0.5 rounded-full">
                    <CheckCircle2 className="w-2.5 h-2.5 inline mr-1 text-emerald-400" />{a}
                  </span>
                ))}
            </div>
            {room.notes && (
              <p className="text-xs text-slate-500 italic mt-1">{room.notes}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
